import { useState } from 'react'
import { AlertTriangle, FileText, Hash, Receipt, Save, TrendingUp } from 'lucide-react'
import { Button, Card, EmptyState, ErrorState, Input, LoadingState, useToast } from '@/components/ui'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  useContractsOverview,
  useNextContractNumber,
  useSetNextContractNumber,
} from '../api/contractsApi.ts'

export function OverviewSection() {
  const a = useAnonymize()
  const { data, isLoading, error, refetch } = useContractsOverview()

  if (isLoading) return <LoadingState label="Lade Kennzahlen …" />
  if (error) {
    return (
      <ErrorState
        description="Kennzahlen konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }
  if (!data) {
    return (
      <EmptyState
        title="Noch keine Kennzahlen"
        description="Sobald Verträge angelegt sind, erscheinen hier Übersichtszahlen."
      />
    )
  }

  const kpis = [
    { label: 'Aktive Verträge', value: String(data.activeContracts), icon: FileText },
    { label: 'Verträge gesamt', value: String(data.totalContracts), icon: FileText },
    { label: 'Monatsumsatz (geplant)', value: a.fmtCurrency(data.totalMonthlyRevenue ?? 0), icon: TrendingUp },
    { label: 'Deckungsbeitrag', value: a.fmtCurrency(data.totalMargin ?? 0), icon: TrendingUp },
    { label: 'Abweichungen', value: String(data.deviations), icon: AlertTriangle },
    { label: 'Offene Abrechnungen', value: String(data.pendingBilling), icon: Receipt },
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label}>
            <Card.Body>
              <div className="flex items-center gap-2 mb-1">
                <kpi.icon className="w-4 h-4 text-gray-500" />
                <p className="text-caption text-gray-500">{kpi.label}</p>
              </div>
              <p className="text-h3 text-gray-900">{kpi.value}</p>
            </Card.Body>
          </Card>
        ))}
      </div>

      <NextNumberCard />

      <Card>
        <Card.Header>
          <Card.Title>Hinweis</Card.Title>
        </Card.Header>
        <Card.Body>
          <p className="text-small text-gray-600">
            Monatsumsatz und Deckungsbeitrag werden mit Etappe 2 (Tarife) und Etappe 5 (Abrechnung)
            berechnet. Die Kacheln „Abweichungen" und „Offene Abrechnungen" werden mit Etappe 4
            (Reconciliation) bzw. Etappe 5 lebendig.
          </p>
        </Card.Body>
      </Card>
    </div>
  )
}

function NextNumberCard() {
  const { toast } = useToast()
  const { data } = useNextContractNumber()
  const set = useSetNextContractNumber()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')

  function handleEdit() {
    setValue(String(data?.next ?? 1))
    setEditing(true)
  }

  function handleSave() {
    const n = Number(value)
    if (!Number.isFinite(n) || n < 1) {
      toast({ title: 'Ungültiger Wert', tone: 'danger' })
      return
    }
    set.mutate(n, {
      onSuccess: (r) => {
        toast({ title: `Nächste Vertragsnummer auf ${r.next} gesetzt`, tone: 'success' })
        setEditing(false)
      },
      onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
    })
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-gray-500" />
          <Card.Title>Vertragsnummer-Counter</Card.Title>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex items-end gap-3">
          {editing ? (
            <>
              <Input
                label="Nächste Vertragsnummer"
                type="number"
                value={value}
                onChange={(e) => setValue(e.currentTarget.value)}
              />
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Save className="w-4 h-4" />}
                onClick={handleSave}
                loading={set.isPending}
              >
                Speichern
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Abbrechen
              </Button>
            </>
          ) : (
            <>
              <div>
                <p className="text-caption text-gray-500">Nächste Vertragsnummer</p>
                <p className="text-h3 text-gray-900">{data?.next ?? '—'}</p>
              </div>
              <Button variant="secondary" size="sm" onClick={handleEdit}>
                Anpassen
              </Button>
            </>
          )}
        </div>
        <p className="text-caption text-gray-500 mt-2">
          Wird beim Anlegen automatisch fortlaufend vergeben. Bei der Übernahme aus SAP hier den
          Startwert setzen (z. B. 310, wenn SAP bei 309 endete).
        </p>
      </Card.Body>
    </Card>
  )
}
